import { createGui } from '@App/app/entities/layer/create-gui.model';
import { Coordinates3 } from '@App/app/entities/layer/measurements/coordinates';
import { Tube, ViewerTubeLayer } from '@App/app/entities/layer/tube.model';
import { CreationToolError } from '@App/app/shared/exceptions/creation-tool-error';
import { Injectable } from '@angular/core';
import { AbstractMesh, Vector3 } from 'babylonjs';
import { MESH } from '@App/app/configs/babylon.config';
import { PRECISION } from '@App/app/configs/precision.config';
import { BabylonNodesService } from '../../babylon-nodes-service/babylon-nodes.service';
import { GenerateGUIService } from '../../gui-services/generate-gui-service/generate-gui.service';
import { MaterialService } from '../../material-service/material.service';
import { SceneService } from '../../scene-service/scene.service';
import { UtilsService } from '../../utils-service/utils.service';
import {
  calculateRadiusInner as calcRadiusInner,
  calculateRadiusOuter as calcRadiusOuter,
  findEdges as findTubeEdges,
} from './tube-geometry.helpers';

@Injectable({
  providedIn: 'root',
})
export class TubeUtilsService implements createGui {
  constructor(
    public generateGUIService: GenerateGUIService,
    private materialService: MaterialService,
    private nodeService: BabylonNodesService,
    private sceneService: SceneService,
    private utilsService: UtilsService,
  ) {}

  calculateTubeData(
    centers: [Vector3, Vector3],
    inners: [Vector3[], Vector3[]] = [[], []],
  ): { edges: [Vector3, Vector3]; radiusOuter: number; radiusInner: number } {
    const direction = centers[1].subtract(centers[0]).normalizeToNew();
    const radiusOuter = this.calculateRadiusOuter(centers, direction);
    if (radiusOuter <= 0) {
      throw new CreationToolError('Outer radius not valid');
    }
    const radiusInner = this.calculateRadiusInner(centers, inners, direction);
    const edges = this.findEdges(centers, direction, radiusOuter);
    if (edges.some((edge) => !edge)) {
      throw new CreationToolError('Mesh edges not find');
    }
    return { edges, radiusOuter, radiusInner };
  }

  changeSampleMaterial(sample: AbstractMesh, defaultMaterial: boolean) {
    sample.material = defaultMaterial
      ? this.materialService.electricBlueMaterial
      : this.materialService.redMaterial;
  }

  createTube(edges: [Vector3, Vector3], radiusOuter: number, radiusInner: number) {
    const tube = new Tube();
    tube.name = `tube`;
    tube.isVisible = true;
    tube.isSaved = true;
    tube.data.edges = [
      { x: edges[0].x, y: edges[0].y, z: edges[0].z },
      { x: edges[1].x, y: edges[1].y, z: edges[1].z },
    ];
    tube.data.length = this.utilsService.getLengthBetweenPoints(...edges);
    tube.data.radiusOuter = +radiusOuter.toFixed(PRECISION.TUBE_RADIUS_DECIMALS);
    tube.data.radiusInner = radiusInner ? +radiusInner.toFixed(PRECISION.TUBE_RADIUS_DECIMALS) : 0;
    return tube;
  }

  showTube(tube: Tube, tubeEdges: [Coordinates3, Coordinates3]) {
    const newTube = tube as ViewerTubeLayer;
    const { radiusOuter, radiusInner } = newTube.data;
    const edges: [Vector3, Vector3] = [
      new Vector3(tubeEdges[0].x, tubeEdges[0].y, tubeEdges[0].z),
      new Vector3(tubeEdges[1].x, tubeEdges[1].y, tubeEdges[1].z),
    ];
    newTube.data.length = Vector3.Distance(edges[0], edges[1]);
    newTube.mesh = this.nodeService.createTube(
      edges,
      { radiusOuter, radiusInner },
      `${MESH.tube.defaultName}${newTube.id}`,
    );
    newTube.mesh.visibility = 0.6;
    newTube.centerLine = this.nodeService.createLine(edges);
    newTube.gui = this.createGui(newTube);
    // init gizmos here
    return newTube;
  }

  createGui(layer: ViewerTubeLayer) {
    if (layer.mesh) {
      return this.generateGUIService.createNameTag(layer.name, layer.mesh, true, layer.id);
    }
  }

  private findEdges(centers: [Vector3, Vector3], direction: Vector3, radius: number) {
    return findTubeEdges(this.sceneService.scene, centers, direction, radius);
  }

  private calculateRadiusOuter(centers: [Vector3, Vector3], direction: Vector3) {
    return calcRadiusOuter(this.sceneService.scene, centers, direction);
  }

  private calculateRadiusInner(
    centers: [Vector3, Vector3],
    inners: [Vector3[], Vector3[]] = [[], []],
    direction: Vector3,
  ) {
    return calcRadiusInner(centers, inners, direction);
  }
}
